// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import deletePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/DeletePaymentProfile";
import updateEditModal from "@insite/client-framework/Store/Pages/SavedPayments/Handlers/UpdateEditModal";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { PaymentProfilesContext } from "@insite/content-library/Pages/SavedPaymentsPage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import blockMarginField from "@store/Library/Components/Toolkit/Block/fields/margin";
import blockPreserveSpacingField from "@store/Library/Components/Toolkit/Block/fields/preserveSpacing";
import blockVariantField from "@store/Library/Components/Toolkit/Block/fields/variant";
import blockVariantSideField from "@store/Library/Components/Toolkit/Block/fields/variantSide";
import SavedPaymentsCardIcon from "@store/Library/Components/Toolkit/SavedPayments/SavedPaymentsCardIcon";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { savedPaymentsProps } from "@store/Themes/common/props/savedPayments";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

const enum fields {
    variant = "variant",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    blockMargin = "blockMargin",
    blockPreserveSpacing = "blockPreserveSpacing",
}
/* Starter Custom Code - End */

const mapDispatchToProps = {
    updateEditModal,
    deletePaymentProfile,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedPaymentsDefaultCardStyles {
    titleText?: TypographyProps;
    mainWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    cardTypeImage?: LazyImageProps;
    descriptionText?: TypographyPresentationProps;
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    nameOnCardLabelText?: TypographyPresentationProps;
    nameOnCardText?: TypographyPresentationProps;
    expirationLabelText?: TypographyPresentationProps;
    expirationText?: TypographyPresentationProps;
    centerColumnGridItem?: GridItemProps;
    billingAddressLabelText?: TypographyPresentationProps;
    billingAddressText?: TypographyPresentationProps;
    rightColumnGridItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    deleteLinkGridItem?: GridItemProps;
    deleteLink?: LinkPresentationProps;
    deleteCardModal?: TwoButtonModalStyles;
    // Starter Custom Code
    block?: BlockProps;
    cardListContainer?: GridContainerProps;
    cardListGridItem?: GridItemProps;
    descriptionTextNote?: TypographyPresentationProps;
    cardInfoSubContainer?: GridContainerProps;
    cardInfoSubGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
}

export const defaultCardStyles: SavedPaymentsDefaultCardStyles = {
    mainWrapper: {
        css: css`
            ${boxStyles()};
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
        `,
    },
    titleText: {
        forwardAs: "h2",
        variant: "h5",
        css: css`
            margin-bottom: ${pxToRem(15)};
        `,
    },
    cardListContainer: savedPaymentsProps.listContainer,
    cardListGridItem: savedPaymentsProps.listGridItem,
    headerWrapper: savedPaymentsProps.card?.descriptionWrapper,
    descriptionText: savedPaymentsProps.card?.descriptionText,
    descriptionTextNote: savedPaymentsProps.card?.descriptionTextNote,
    container: savedPaymentsProps.card?.container,
    leftColumnGridItem: savedPaymentsProps.card?.gridItem,
    centerColumnGridItem: savedPaymentsProps.card?.gridItem,
    cardInfoSubContainer: savedPaymentsProps.card?.infoContainer,
    cardInfoSubGridItem: savedPaymentsProps.card?.infoGridItem,
    billingAddressLabelText: savedPaymentsProps.card?.infoLabel,
    billingAddressText: savedPaymentsProps.card?.infoValue,
    nameOnCardLabelText: savedPaymentsProps.card?.infoLabel,
    nameOnCardText: savedPaymentsProps.card?.infoValue,
    expirationLabelText: savedPaymentsProps.card?.infoLabel,
    expirationText: savedPaymentsProps.card?.infoValue,
    actionsWrapper: savedPaymentsProps.card?.actionsWrapper,
    editLink: savedPaymentsProps.card?.editLink,
    deleteLink: savedPaymentsProps.card?.deleteLink,
};

const styles = defaultCardStyles;

const getImageName = (cardType: string) => {
    if (cardType.toLowerCase() === "american express") {
        return "amex";
    }
    if (cardType.toLowerCase() === "discover") {
        return "discover";
    }
    if (cardType.toLowerCase() === "mastercard") {
        return "mc";
    }
    if (cardType.toLowerCase() === "visa") {
        return "visa";
    }
    return "";
};

const SavedPaymentsDefaultCard: React.FC<Props> = ({
    updateEditModal,
    deletePaymentProfile,
    // Starter Custom Code
    fields: { blockVariant, blockVariantSide, blockMargin },
}) => {
    const toasterContext = useContext(ToasterContext);
    const [deleteCardModalIsOpen, setDeleteCardModalIsOpen] = React.useState(false);

    const paymentProfilesDataView = usePersistentDataView(useContext(PaymentProfilesContext)); // Starter Custom Code

    if (!paymentProfilesDataView.value) {
        return null;
    }

    const defaultCard = paymentProfilesDataView.value.find(o => o.isDefault);
    if (!defaultCard) {
        return null;
    }

    const editClickHandler = () => {
        updateEditModal({ paymentProfile: defaultCard, modalIsOpen: true });
    };

    const deleteCancelHandler = () => {
        setDeleteCardModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        deletePaymentProfile({
            paymentProfileId: defaultCard.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Card Deleted"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const deleteClickHandler = () => {
        setDeleteCardModalIsOpen(true);
    };

    return (
        <Block /* Starter Custom Code */
            variant={blockVariant}
            variantSide={blockVariantSide}
            margin={blockMargin}
            {...styles.block}
        >
            <GridContainer {...styles.cardListContainer} /* Starter Custom Code */>
                <GridItem {...styles.cardListGridItem} /* Starter Custom Code */>
                    <StyledWrapper {...styles.mainWrapper} data-test-selector="defaultCard" /* Starter Custom Code */>
                        {/* Starter Custom Code - Start */}
                        <Typography {...styles.titleText}>{translate("Default Card")}</Typography>
                        <StyledWrapper {...styles.headerWrapper}>
                            <SavedPaymentsCardIcon cardType={defaultCard.cardType} />
                            <Typography {...styles.descriptionText}>
                                {defaultCard.description || defaultCard.cardType}
                                <Typography {...styles.descriptionTextNote}>
                                    {`${defaultCard.cardType} ${translate(
                                        "ending in",
                                    )} ${defaultCard.maskedCardNumber.substring(
                                        defaultCard.maskedCardNumber.length - 4,
                                    )}`}
                                </Typography>
                            </Typography>
                        </StyledWrapper>
                        {/* Starter Custom Code - End */}
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.leftColumnGridItem}>
                                <GridContainer {...styles.cardInfoSubContainer} /* Starter Custom Code */>
                                    <GridItem {...styles.cardInfoSubGridItem} /* Starter Custom Code */>
                                        <Typography {...styles.nameOnCardLabelText} id="nameOnCard">
                                            {translate("Name on Card")}
                                        </Typography>
                                        <Typography {...styles.nameOnCardText} aria-labelledby="nameOnCard">
                                            {defaultCard.cardHolderName}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.cardInfoSubGridItem} /* Starter Custom Code */>
                                        <Typography {...styles.expirationLabelText} id="expiration">
                                            {translate("Expiration")}
                                        </Typography>
                                        <Typography {...styles.expirationText} aria-labelledby="expiration">
                                            {defaultCard.expirationDate}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.centerColumnGridItem}>
                                <Typography {...styles.billingAddressLabelText} id="billingAddress">
                                    {translate("Billing Address")}
                                </Typography>
                                <Typography {...styles.billingAddressText} aria-labelledby="billingAddress">
                                    {[
                                        defaultCard.address1,
                                        defaultCard.address2,
                                        defaultCard.address3,
                                        defaultCard.address4,
                                    ]
                                        .filter(o => !!o)
                                        .join(", ")}
                                    <br />
                                    {defaultCard.city}, {defaultCard.state}
                                    <br />
                                    {defaultCard.postalCode}
                                    <br />
                                    {defaultCard.country}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                        {/* Starter Custom Code - Start */}
                        <StyledWrapper {...styles.actionsWrapper}>
                            <Link {...styles.editLink} onClick={editClickHandler} data-test-selector="editButton">
                                {translate("Edit")}
                            </Link>
                            <Link {...styles.deleteLink} onClick={deleteClickHandler} data-test-selector="deleteButton">
                                {translate("Delete")}
                            </Link>
                        </StyledWrapper>
                        {/* Starter Custom Code - End */}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <TwoButtonModal
                {...styles.deleteCardModal}
                modalIsOpen={deleteCardModalIsOpen}
                headlineText={translate("Delete Card")}
                messageText={translate("Are you sure you want to delete this card?")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="submitDeleteDefaultCardButton"
            />
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedPaymentsDefaultCard),
    definition: {
        displayName: "Default Card",
        group: "Saved Payments",
        allowedContexts: ["SavedPaymentsPage"],
        /* Starter Custom Code - Start */
        fieldDefinitions: [
            {
                ...blockVariantField,
                name: fields.blockVariant,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
            },
            {
                ...blockMarginField,
                name: fields.blockMargin,
            },
            {
                ...blockPreserveSpacingField,
                name: fields.blockPreserveSpacing,
            },
        ],
        /* Starter Custom Code - End */
    },
};

export default widgetModule;
